import React, { useState, useMemo } from 'react';
import { List, useTable, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { AdminSurveyResponseListItemDto, ResponseStatus } from '@wellverse/shared-ts';

const statusColors: Record<ResponseStatus, string> = {
  [ResponseStatus.IN_PROGRESS]: '#f5a623',
  [ResponseStatus.COMPLETED]: '#3ecf8e',
  [ResponseStatus.ABANDONED]: '#ff6b6b',
};

export const SurveyResponseList: React.FC = () => {
  const { tableProps } = useTable<AdminSurveyResponseListItemDto>({
    resource: 'survey-responses',
    pagination: { current: 1, pageSize: 20 },
  });

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    const lower = searchText.toLowerCase();
    return tableProps.dataSource?.filter((item: any) =>
      item.surveyName?.toLowerCase().includes(lower) ||
      item.userId?.toLowerCase().includes(lower)
    );
  }, [tableProps.dataSource, searchText]);

  return (
    <List resource="survey-responses">
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by survey name or user ID..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
      </div>
      <Table {...tableProps} dataSource={filteredData} rowKey="id">
        <Table.Column dataIndex="surveyName" title="Survey" />
        <Table.Column dataIndex="userId" title="User ID" />
        <Table.Column
          dataIndex="status"
          title="Status"
          filters={Object.values(ResponseStatus).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: any) => record.status === value}
          render={(status: ResponseStatus) => (
            <Tag
              style={{
                borderColor: statusColors[status] || '#898989',
                color: statusColors[status] || '#898989',
                background: `${statusColors[status] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {status}
            </Tag>
          )}
        />
        <Table.Column dataIndex="currentScreenIndex" title="Screen Index" />
        <Table.Column dataIndex="answerCount" title="Answers" />
        <Table.Column
          dataIndex="startedAt"
          title="Started At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="completedAt"
          title="Completed At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: AdminSurveyResponseListItemDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="survey-responses" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="survey-responses" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
